import {
  changePercent, compactCurrency, currencyFormatter, formatYMD, getComparisonRange,
} from './reportingUtils';
import { getActivePreset } from '../components/DateRangePicker';

// WHY: Reporting::DashboardQuery::BEHIND_PACE_RATIO (0.97) mirrors 1 - ON_PACE_BAND; change both together.
const ON_PACE_BAND = 0.03;
const NEXT_DAYS = 30;

const count = (value) => (value == null ? '—' : Number(value).toLocaleString());

export const metricChange = (current, prior, label) => {
  if (current == null) return undefined;
  const pct = changePercent(current, prior);
  if (pct == null) return undefined;
  const n = parseFloat(pct);
  let direction = 'flat';
  if (n > 0) direction = 'up';
  if (n < 0) direction = 'down';
  return { value: `${Math.abs(n).toFixed(1)}%`, direction, label };
};

export const netMembersChange = (net) => {
  if (net == null) return undefined;
  let direction = 'flat';
  if (net > 0) direction = 'up';
  if (net < 0) direction = 'down';
  return { value: `${Math.abs(net).toLocaleString('en-US')} net`, direction };
};

export const formatPercent = (value) => (value == null ? '—' : `${value}%`);

const YOY_LABEL = 'vs same period last year';
const YOY_PRESETS = ['this_year', 'year_to_date', 'last_year'];

const yearEarlier = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return formatYMD(new Date(y - 1, m - 1, Math.min(d, new Date(y - 1, m, 0).getDate())));
};

const priorIsLastYear = (dateRange) => {
  const prior = getComparisonRange(dateRange);
  return prior.start === yearEarlier(dateRange.start) && prior.end === yearEarlier(dateRange.end);
};

export const showYearOverYear = (dateRange) => Boolean(dateRange?.start && dateRange?.end)
  && !YOY_PRESETS.includes(getActivePreset(dateRange))
  && !priorIsLastYear(dateRange);

// WHY: keys and the 3–9 limit mirror TeamMembership::REPORTING_DASHBOARD_KPI_KEYS / _COUNT; change both together.
export const KPI_GROUPS = ['Sales', 'Shows', 'Audience', 'Money'];
export const DEFAULT_KPI_KEYS = ['net_revenue', 'tickets_sold', 'sell_through', 'check_in_rate', 'per_cap', 'show_profit'];
export const MIN_KPI_CARDS = 3;
export const MAX_KPI_CARDS = 9;

export const buildKpiCatalog = (k, label, { dateMode, yearOverYear = false } = {}) => {
  const p = k.prior || {};
  const lastYear = yearOverYear ? k.last_year : null;
  const revenueCaption = dateMode === 'order_date'
    ? 'All streams by sale date, net to organizer'
    : 'Show streams by event date, others by purchase date';
  const card = (key, group, cardLabel, field, value, caption) => ({
    key,
    group,
    label: cardLabel,
    value,
    change: metricChange(k[field], p[field], label),
    yoyChange: lastYear ? metricChange(k[field], lastYear[field], YOY_LABEL) : undefined,
    caption,
  });
  return [
    card('net_revenue', 'Sales', 'Net revenue', 'net_revenue', compactCurrency(k.net_revenue || 0), revenueCaption),
    card('tickets_sold', 'Sales', 'Tickets sold', 'tickets_sold', count(k.tickets_sold), `${count(k.ticketed_events)} ticketed events`),
    card('ticket_revenue', 'Sales', 'Ticket revenue', 'gross_tickets_revenue', compactCurrency(k.gross_tickets_revenue || 0), 'Admission, net to organizer'),
    card('addons_sold', 'Sales', 'Add-ons sold', 'addons_sold', count(k.addons_sold), 'Parking, merch, and upgrades'),
    card('gross_addons', 'Sales', 'Add-on revenue', 'gross_addons_revenue', compactCurrency(k.gross_addons_revenue || 0), 'Add-ons, net to organizer'),
    card('all_events', 'Shows', 'All events', 'all_events', count(k.all_events), 'Confirmed events in range'),
    card('ticketed_events', 'Shows', 'Ticketed events', 'ticketed_events', count(k.ticketed_events), 'Confirmed events with tickets'),
    card('announced', 'Shows', 'Announced', 'announced', count(k.announced), 'Publicly announced'),
    card('on_sale', 'Shows', 'On sale', 'on_sale', count(k.on_sale), 'Currently selling'),
    card('sell_through', 'Shows', 'Sell-through', 'sell_through', formatPercent(k.sell_through), 'Of capacity, shows in range'),
    card('check_in_rate', 'Audience', 'Check-in rate', 'check_in_rate', formatPercent(k.check_in_rate), `${count(k.scan_count)} scanned at the door`),
    card('per_cap', 'Audience', 'Per-cap spend', 'per_cap', k.per_cap == null ? '—' : currencyFormatter.format(k.per_cap), 'F&B + add-ons per scan'),
    card('show_profit', 'Money', 'Show profit', 'ecm', compactCurrency(k.ecm || 0), `From Event P&L, ${count(k.settled_count)} settled`),
    card('phab', 'Money', 'PHAB', 'phab', currencyFormatter.format(k.phab || 0), 'Per head average, bar'),
    card('fnb_revenue', 'Money', 'F&B revenue', 'fnb_revenue', compactCurrency(k.fnb_revenue || 0), 'From POS sales'),
  ];
};

export const buildKpiCards = (k, label, options, keys = DEFAULT_KPI_KEYS) => {
  const byKey = Object.fromEntries(buildKpiCatalog(k, label, options).map((c) => [c.key, c]));
  return keys.map((key) => byKey[key]).filter(Boolean);
};

export const paceStatus = (event) => {
  if (event.total_capacity > 0 && event.total_tickets_sold >= event.total_capacity) return 'sold_out';
  if (event.expected_finish == null || !event.forecast) return null;
  const ratio = event.expected_finish / event.forecast;
  if (ratio > 1 + ON_PACE_BAND) return 'ahead';
  if (ratio >= 1 - ON_PACE_BAND) return 'on_pace';
  return 'behind';
};

export const PACING_TABS = [
  { value: 'next_30', label: 'Next 30 days' },
  { value: 'needs_push', label: 'Needs a push' },
  { value: 'all', label: 'All upcoming' },
];

export const filterPacing = (events, tab) => {
  if (tab === 'next_30') return events.filter((e) => e.days_until != null && e.days_until <= NEXT_DAYS);
  if (tab === 'needs_push') return events.filter((e) => paceStatus(e) === 'behind');
  return events;
};

export const pacingFooter = (loaded, total) => (total > loaded ? `Showing ${loaded} of ${total} upcoming events` : null);

export const dayBadge = (daysUntil) => {
  if (daysUntil === 0) return { label: 'Today', color: 'error' };
  if (daysUntil === 1) return { label: 'Tomorrow', color: 'error' };
  return { label: `${daysUntil}d`, color: 'gray' };
};

// WHY: many confirms never pass through an offer, so confirms ÷ offers can top 100%; the card's Hold → confirm stat covers it.
const CONFIRMS_STEP = 2;
const ANNOUNCED_STEP = 3;

export const buildFunnel = (booking = {}, kpis = {}, { crossBasis = false } = {}) => {
  const steps = [
    ['Holds', booking.holds], ['Offers', booking.offers], ['Confirms', booking.confirms],
    ['Announced', kpis.announced], ['On sale', kpis.on_sale],
  ].map(([label, value]) => ({ label, count: value || 0 }));
  const max = Math.max(...steps.map((s) => s.count), 1);
  return steps.map((step, i) => ({
    ...step,
    width: Math.round((step.count / max) * 100),
    conversion: i === 0 || i === CONFIRMS_STEP || !steps[i - 1].count || (crossBasis && i === ANNOUNCED_STEP)
      ? null
      : Math.round((step.count / steps[i - 1].count) * 100),
  }));
};

export const selectBriefInsights = (insights) => (insights || []).slice(0, 3);
